<template>
  <!-- Jeśli dostępny jest niestandardowy szablon, renderujemy wygenerowany HTML -->
  <div v-if="customTemplate" v-html="generatedHtml"  class="products__single-product-container q-col-sm-12 q-col-md-6" ></div>
  <!-- W przeciwnym razie renderujemy domyślną strukturę -->
  <div v-else :key="result.id" class="products__single-product-container q-col-sm-12 q-col-md-6">
    <div class="products__single-product q-p-1 q-position-relative q-d-flex">
      <div class="products__single-product-image-container q-col-4 q-col-md-5 q-p-0">
        <img class="products__single-product-image q-p-1 q-img-fluid" :src="staticImage" :alt="result.title" :width="width" :height="height">
      </div>
      <div class="products__single-product-info q-col-8 q-col-md-7 q-px-2 q-text-black q-text-start">
        <p class="products__single-product-title q-p-0 q-m-0 q-fw-semibold">
          {{ result.title }}
        </p>
        <AutocompletePriceBox :product="firstVariant" />
        <a :href="actualLink" @click="handleClick" class="q-stretched-link"></a>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, defineProps, inject, ref, toRefs } from 'vue';
import { generateStaticImage, addDisplayMacros, formatPrice, parsePrice, getDiscount, getRealPrice } from '@/core/utils';
import { createTemplate } from '@/core/Template';
import { SmartSearchConfigInterface } from '@/core/types';
import AutocompletePriceBox from '@/components/AutocompletePopup/AutocompletePriceBox.vue';

// Rozszerzamy props o customTemplate, który będzie zawierał HTML szablonu z endpointu
const props = defineProps(['result', 'customTemplate']);
const { link, link_click, variants, id } = toRefs(props.result);
const firstVariant = ref(variants.value[0]);

// Pobranie konfiguracji
const SmartSearchConfig = ref(inject<SmartSearchConfigInterface>('SmartSearchConfig')!);
const { width, height } = SmartSearchConfig.value.onfocus.productImageSize;
const { useQuarticImages } = SmartSearchConfig.value.serp.productImageSize;
const { currency: currencyCode, removeDecimals } = SmartSearchConfig.value.appConfiguration;

// Obliczanie ścieżki do obrazu
const staticImage = computed(() => {
  const imageLink = firstVariant.value.image_link;
  if (!useQuarticImages) {
    return imageLink;
  }
  return imageLink
      ? generateStaticImage(imageLink, width, height)
      : generateStaticImage('https://static.quartic.com.pl/img/no-image/no-image.png', width, height);
});

// Obsługa linku
const actualLink = ref(link.value);
const handleClick = () => {
  actualLink.value = link_click.value.replace(/^http:/, 'https:');
};

// Dodatkowe funkcje i obliczenia dla makr
const secondPrice = firstVariant.value.old_price || firstVariant.value.sale_price;
const { price, oldPrice } = getRealPrice(parsePrice(firstVariant.value.price), parsePrice(secondPrice));
const omnibusPrice = parsePrice(firstVariant.value.omnibus_price);
const formattedPrice = formatPrice(price, currencyCode, removeDecimals);
const formattedPriceOld = formatPrice(oldPrice, currencyCode, removeDecimals);
const formattedOmnibusPrice = formatPrice(omnibusPrice, currencyCode, removeDecimals);
const discount = getDiscount(price, oldPrice);

// Funkcja przygotowująca czysty link z modyfikacją protokołu
const prepareClearLink = (link: string, clickLink: string) => {
  return ` href="${link}" data-href="${clickLink.replace(/^http:/, 'https:')}" onmousedown="return this.href = this.getAttribute('data-href');" ontouchstart="return this.href = this.getAttribute('data-href');" `;
};

// Mechanizm generowania HTML przy użyciu customTemplate
const generatedHtml = ref('');
if (props.customTemplate) {
  // Rozszerzamy dane produktu o dodatkowe właściwości (makra)
  firstVariant.value.discount = discount;
  firstVariant.value.image_link_cdn = staticImage.value;
  firstVariant.value.price_formatted = formattedPrice;
  firstVariant.value.price_number = price;
  firstVariant.value.old_price_formatted = formattedPriceOld;
  firstVariant.value.old_price_number = oldPrice;
  firstVariant.value.omnibus_price_formatted = formattedOmnibusPrice;
  firstVariant.value.omnibus_price_number = omnibusPrice;
  firstVariant.value.id = id.value;
  firstVariant.value.link_click = link_click.value;
  firstVariant.value.clear_link = prepareClearLink(link.value, link_click.value);

  // Możemy dodatkowo modyfikować obiekt za pomocą funkcji addDisplayMacros
  addDisplayMacros(firstVariant.value);

  // Generujemy HTML na podstawie niestandardowego szablonu oraz danych produktu
  generatedHtml.value = createTemplate(props.customTemplate, firstVariant.value, variants.value);
}
</script>

<style scoped>
.products__single-product-image {
  height: auto;
  width: auto;
  margin: auto;
  min-width: 100px;
  max-width: 100%;
  max-height: 135px;
}
@media (max-width: 768px) {
  .products__single-product-image {
    height: auto;
    width: auto;
    margin: auto;
    min-width: 100px;
    max-width: 100%;
    max-height: 135px;
  }
}

.products__single-product-title {
  font-size: 0.75rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.products__single-product {
  border-bottom: 1px solid #f0f2f6;
  min-height: 100%;
  transition: background-color 0.3s ease;
}
.products__single-product:hover {
  background-color: #f2f2f2;
}

.q-stretched-link {
  cursor: pointer;
}
</style>
